'use client';

import Button, { ButtonVariants } from '@pixum/button';
import Text, { TEXT_VARIANT } from '@pixum/text';
import { Dialog, DialogContent, DialogFooter, DIALOG_ROLES } from '@pixum/web-dialog';
import type { LinkNode } from 'data-layer';

interface CartDialogAlertShippingAddressProps {
  show: boolean;
  onClose: () => void;
  constants: Record<string, string>;
  links?: LinkNode[];
}

/**
 * Shipping address error dialog for PayPal Express.
 *
 * Shown when approveOrder returns 422 with code 'PAYPAL_INVALID_ADDRESS'.
 * The user is prompted to cancel or change their shipping address.
 */
export function CartDialogAlertShippingAddress({
  show,
  onClose,
  constants,
  links,
}: CartDialogAlertShippingAddressProps) {
  const headline = constants['i18n_cart_shipping_address_error_headline'];
  const message = constants['i18n_cart_shipping_address_error_message'];
  const countryLinkText = constants['i18n_countries_overview'];
  const cancelLabel = constants['i18n_cancel'];
  const changeAddressLabel = constants['i18n_change_Address'];
  const countryUrl = links?.find(link => link.id === 'select-country')?.url;

  // Split message on %selectCountry% placeholder to embed a link to the country selection page.
  // Falls back to plain text if the placeholder is absent or the URL is missing.
  const [messagePrefix = '', messageSuffix = ''] = message?.split('%selectCountry%') ?? [];

  return (
    <Dialog
      content={
        <DialogContent>
          <>
            <Text variant={TEXT_VARIANT.HEADLINE}>{headline}</Text>
            <Text>
              {messagePrefix}
              {countryLinkText && countryUrl && (
                <a href={countryUrl} rel="noreferrer">
                  {countryLinkText}
                </a>
              )}
              {messageSuffix}
            </Text>
          </>
        </DialogContent>
      }
      footer={
        <DialogFooter
          left={
            <Button label={cancelLabel} onClick={onClose} variant={ButtonVariants.TERTIARY}>
              {cancelLabel}
            </Button>
          }
          right={
            <Button label={changeAddressLabel} onClick={onClose} variant={ButtonVariants.TERTIARY}>
              {changeAddressLabel}
            </Button>
          }
          role={DIALOG_ROLES.ALERT}
        />
      }
      onClose={onClose}
      open={show}
      role={DIALOG_ROLES.ALERT}
      showCloseButton={false}
    />
  );
}
